import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getDeviceBreakdownApi} from '../../../modules/Dashboard/DashboardCURD'

/* ─── Types ─── */
interface DeviceBreakdownItem {
  deviceTypeID: number
  deviceTypeName: string
  totalCount: number
  percentage: number
}
interface DeviceBreakdownResp {
  isSuccess: boolean
  message: string
  data: DeviceBreakdownItem[]
}
interface IState {
  loading: boolean
  items: DeviceBreakdownItem[]
  error: string
}

// Add this helper at the top of the file, outside the component
function formatIndian(v: number): string {
  if (v >= 10_000_000) return `${(v / 10_000_000).toFixed(2)} Cr`
  if (v >= 100_000) return `${(v / 100_000).toFixed(2)} L`
  //   if (v >= 1_000)      return `${(v / 1_000).toFixed(1)} K`
  return v.toLocaleString('en-IN')
}

/* ─── Helpers ─── */
const DEVICE_ICONS: Record<string, string> = {
  mobile: 'bi bi-phone-fill',
  android: 'bi bi-phone-fill',
  ios: 'bi bi-apple',
  web: 'bi bi-globe',
  desktop: 'bi bi-pc-display',
  tablet: 'bi bi-tablet-fill',
  laptop: 'bi bi-laptop',
}
function getDeviceIcon(name: string): string {
  const lower = (name || '').toLowerCase()
  for (const key of Object.keys(DEVICE_ICONS)) {
    if (lower.includes(key)) return DEVICE_ICONS[key]
  }
  return 'bi bi-device-hdd-fill'
}
const PALETTE = ['#cb1e46', '#185FA5', '#1D9E75', '#f59e0b', '#8b5cf6', '#06b6d4']

/* ─── API ─── */
async function fetchDeviceBreakdown(memberId = 0): Promise<DeviceBreakdownResp> {
  const response = await getDeviceBreakdownApi(memberId)
  return response.data
}

/* ─── Chart ─── */
function buildDonutOptions(labels: string[], series: number[], colors: string[]): ApexOptions {
  return {
    chart: {
      type: 'donut',
      height: 230,
      fontFamily: 'inherit',
      animations: {enabled: true, easing: 'easeinout', speed: 700},
    },
    labels,
    series,
    colors,
    dataLabels: {enabled: false},
    legend: {show: false},
    plotOptions: {
      pie: {
        donut: {
          size: '68%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '13px',
              fontWeight: 600,
              color: '#6b7280',
              formatter: (w) => {
                const t = w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)
                if (t >= 10_000_000) return `${(t / 10_000_000).toFixed(2)}Cr`
                if (t >= 100_000) return `${(t / 100_000).toFixed(2)}L`
                return t.toLocaleString('en-IN')
              },
            },
            value: {
              fontSize: '20px',
              fontWeight: 700,
              color: '#111827',
              formatter: (v) => {
                const n = Number(v)
                if (n >= 10_000_000) return `${(n / 10_000_000).toFixed(2)}Cr`
                if (n >= 100_000) return `${(n / 100_000).toFixed(2)}L`
                return n.toLocaleString('en-IN')
              },
            },
          },
        },
      },
    },
    tooltip: {
      y: {
        formatter: (v) => {
          const n = Number(v)
          if (n >= 10_000_000) return `${(n / 10_000_000).toFixed(2)}Cr`
          if (n >= 100_000) return `${(n / 100_000).toFixed(2)}L`
          return n.toLocaleString('en-IN')
        },
      },
      style: {fontSize: '12px', fontFamily: 'inherit'},
    },
    stroke: {width: 0},
    states: {
      hover: {filter: {type: 'lighten', value: 0.05}},
      active: {filter: {type: 'none'}},
    },
  }
}

/* ─── Component ─── */
const DeviceBreakdownWidget: React.FC<{className?: string; memberId?: number}> = ({
  className = '',
  memberId = 0,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstance = useRef<ApexCharts | null>(null)
  const [state, setState] = useState<IState>({loading: true, items: [], error: ''})

  useEffect(() => {
    setState((s) => ({...s, loading: true, error: ''}))
    fetchDeviceBreakdown(memberId)
      .then((resp) => {
        if (resp.isSuccess) setState({loading: false, items: resp.data ?? [], error: ''})
        else setState({loading: false, items: [], error: resp.message})
      })
      .catch((err) => setState({loading: false, items: [], error: String(err)}))
  }, [memberId])

  useEffect(() => {
    if (state.loading || !chartRef.current || state.items.length === 0) return
    const labels = state.items.map((i) => i.deviceTypeName)
    const series = state.items.map((i) => Number(i.totalCount) || 0)
    const colors = state.items.map((_, idx) => PALETTE[idx % PALETTE.length])
    if (chartInstance.current) {
      chartInstance.current.updateOptions(buildDonutOptions(labels, series, colors))
    } else {
      chartInstance.current = new ApexCharts(
        chartRef.current,
        buildDonutOptions(labels, series, colors)
      )
      chartInstance.current.render()
    }
    return () => {
      chartInstance.current?.destroy()
      chartInstance.current = null
    }
  }, [state.loading, state.items])

  return (
    <>
      <style>{`
        @keyframes dbw-shimmer { 0%,100%{opacity:1} 50%{opacity:.35} }
        .dbw-shimmer { animation:dbw-shimmer 1.4s ease-in-out infinite; background:#f3f4f6; }
      `}</style>

      <div className={`${className}`}>
        {/* ── Header ── */}
        <h5 className='fw-bold mb-3 text-primary'>
          <i className='bi bi-pie-chart me-2' />
          Device Breakdown
        </h5>

        <div className='card-body py-3 px-3 px-md-4'>
          {state.loading ? (
            <>
              {/* Donut skeleton */}
              <div className='d-flex justify-content-center mb-3'>
                <div
                  className='dbw-shimmer rounded-circle position-relative'
                  style={{width: 170, height: 170}}
                >
                  <div
                    className='position-absolute top-50 start-50 translate-middle rounded-circle bg-white'
                    style={{width: 105, height: 105}}
                  />
                </div>
              </div>
              {/* Legend skeleton */}
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className='d-flex align-items-center gap-2 p-2 rounded-3 mb-2'
                  style={{background: '#f9fafb', border: '1px solid #f3f4f6'}}
                >
                  <div
                    className='dbw-shimmer rounded-circle'
                    style={{width: 10, height: 10, flexShrink: 0, animationDelay: `${i * 0.1}s`}}
                  />
                  <div
                    className='dbw-shimmer rounded-2'
                    style={{width: 20, height: 20, animationDelay: `${i * 0.1}s`}}
                  />
                  <div
                    className='dbw-shimmer rounded-2 flex-fill'
                    style={{height: 12, animationDelay: `${i * 0.1}s`}}
                  />
                  <div className='dbw-shimmer rounded-2' style={{width: 55, height: 12}} />
                  <div className='dbw-shimmer rounded-pill' style={{width: 40, height: 20}} />
                </div>
              ))}
            </>
          ) : state.error ? (
            <div
              className='d-flex align-items-center justify-content-center text-danger small'
              style={{minHeight: 260}}
            >
              <i className='bi bi-exclamation-triangle me-2' />
              {state.error}
            </div>
          ) : state.items.length === 0 ? (
            <div
              className='d-flex align-items-center justify-content-center text-muted small'
              style={{minHeight: 260}}
            >
              No data available
            </div>
          ) : (
            <>
              {/* Donut chart — full width on mobile, centered on larger */}
              <div className='w-100' ref={chartRef} />

              {/* Legend rows — stack on mobile, same on all sizes */}
              <div className='d-flex flex-column gap-2 mt-2'>
                {state.items.map((item, idx) => {
                  const color = PALETTE[idx % PALETTE.length]
                  return (
                    <div
                      key={item.deviceTypeID}
                      className='d-flex align-items-center gap-2 px-3 py-2 rounded-3'
                      style={{background: '#f9fafb', border: '1px solid #f3f4f6'}}
                    >
                      {/* dot */}
                      <span
                        className='rounded-circle flex-shrink-0'
                        style={{width: 10, height: 10, background: color, display: 'inline-block'}}
                      />
                      {/* icon */}
                      <i
                        className={`${getDeviceIcon(item.deviceTypeName)} flex-shrink-0`}
                        style={{color, fontSize: 15}}
                      />
                      {/* name — truncate on very small screens */}
                      <span
                        className='flex-fill text-truncate fw-medium small'
                        style={{color: '#374151'}}
                      >
                        {item.deviceTypeName}
                      </span>
                      {/* count — hide on xs, show sm+ */}
                      <span className='d-none d-sm-inline fw-bold small' style={{color: '#111827'}}>
                        {formatIndian(Number(item.totalCount))}
                      </span>
                      {/* percentage badge */}
                      <span
                        className='fw-bold rounded-pill flex-shrink-0'
                        style={{
                          background: color + '1a',
                          color,
                          padding: '2px 8px',
                          fontSize: 11,
                        }}
                      >
                        {Number(item.percentage).toFixed(1)}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export {DeviceBreakdownWidget}
